const defaultIncomeTypes = [
  "gross",
  "overtime_shift_allowance",
  "bonus_commission",
  "gross_secondary",
] as const;

export type IncomeType = (typeof defaultIncomeTypes)[number];

interface RawIncomeItem {
  id?: string;
  hubspotId?: string | null;
  type: string;
  amount: number;
  frequency: string;
  [key: string]: any;
}

interface NormalizedIncomeItem {
  type: string;
  amount: string;
  frequency: string;
  dbId?: string;
  noOfTitleHolders?: string;
  propertyAddress?: string;
  propertyExpense?: string;
  propertyType?: string;
}

export const normalizeIncome = (
  incomeDataFromAPI: RawIncomeItem[]
): NormalizedIncomeItem[] => {
  const filteredData = incomeDataFromAPI.filter(
    (item) => item.type !== "other"
  );

  const mapped = defaultIncomeTypes.map((type) => {
    const match = filteredData.find((item) => item.type === type);

    const base: NormalizedIncomeItem = {
      type,
      amount: match ? match.amount.toString() : "0",
      frequency: match ? match.frequency : "annually",
    };

    if (match?.id || match?.dbId) base.dbId = match.id || match.dbId;

    return base;
  });

  const extra = filteredData
    .filter((item) => !defaultIncomeTypes.includes(item.type as IncomeType))
    .map((item): NormalizedIncomeItem => {
      const base: NormalizedIncomeItem = {
        type: item.type,
        amount: item.amount?.toString() || "0",
        frequency: item.frequency || "annually",
        ...(item.noOfTitleHolders && {
          noOfTitleHolders: item.noOfTitleHolders.toString(),
        }),
        ...(item.propertyAddress && { propertyAddress: item.propertyAddress }),
        ...(item.propertyExpense && {
          propertyExpense: item.propertyExpense.toString(),
        }),
        ...(item.propertyType && { propertyType: item.propertyType }),
      };

      if (item.id || item.dbId) base.dbId = item.id || item.dbId;

      return base;
    });

  return [...mapped, ...extra];
};

type RawIncome = {
  id?: string;
  dbId?: string;
  type: string;
  amount: string;
  frequency?: string;
  propertyType?: string;
  noOfTitleHolders?: string;
  propertyExpense?: string;
  propertyAddress?: string;
};

interface Income {
  dbId?: string;
  type: string;
  amount: string;
  frequency: string;
  propertyType?: string;
  noOfTitleHolders?: string;
  propertyExpense?: string;
  propertyAddress?: string;
}

export function normalizeSavedIncomes(
  savedIncomes: RawIncome[] = []
): Income[] {
  return savedIncomes
    .filter((income) => income.type !== "other")
    .map(
      (income): Income => ({
        dbId: income.id ?? "",
        type: income.type,
        amount: String(income.amount),
        frequency: income.frequency ?? "",
        ...(income.propertyType && { propertyType: income.propertyType }),
        ...(income.propertyAddress && {
          propertyAddress: income.propertyAddress,
        }),
        ...(income.propertyExpense && {
          propertyExpense: income.propertyExpense,
        }),
        ...(income.noOfTitleHolders && {
          noOfTitleHolders: income.noOfTitleHolders ?? "",
        }),
      })
    );
}
